// ─────────────────────────────────────────────
//  roomResolver — a sheet label → the room doc it means. BEST match, never the
//  first one Firestore happened to return.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  ⚠️ WHAT WAS WRONG. `roomForOrRoom` asked "does this room match?" of every
//  room in turn and took the first `true` — with `Array.find`. The rank
//  function that `assignmentsForRoom` computes was thrown away on the reverse
//  lookup. So with the legacy seed rooms present alongside the current ones:
//
//      "WC E1"  → [Endo 1, WC E1]        first-in-array wins
//      "WC OB"  → [OB 1,   WC OB]
//      "WC MRI" → [MRI,    WC MRI]
//
//  …an EXACT-name room lost to a legacy alias because it sorted later.
//  Firestore document order decided which room a case landed in. That is a
//  case in the wrong room, silently, with no error anywhere.
//
//  ⚠️ AND ON A TIE WE REFUSE. Two rooms matching at the SAME strength is not a
//  question this code is allowed to answer — picking one is a coin flip on a
//  clinical location. `resolveRoom` returns `status:"ambiguous"` naming both,
//  which the caller turns into a review item.
//
//  RANKS, strongest first:
//    0 exact       label === room name                    "WC E1" ≡ "wc e1"
//    1 or-prefix   label === room name minus a leading OR  room "OR 3" ← "3"
//    2 alias       ROOM_NAME_ALIASES legacy map            room "Endo 1" ← "wc e1"
//    3 squashed    whitespace-insensitive                  room "WC CL 2" ← "WC CL2"
//
//  A family regex is NOT on this ladder: it decides a CAMPUS, not a room. See
//  roomCampus.js.
//
//  Pure — no Firebase, no React.
// ─────────────────────────────────────────────
// ──────────────────────────────────────
//  ROOM_NAME_ALIASES — legacy board room names → sheet orRoom labels.
//
//  Lives here (rather than in gridTemplate.js, which re-exports it) because it
//  is a MATCHING rule, and because it is the source of the ambiguity above:
//  every one of these keys is a legacy Wesley Chapel seed room whose alias
//  target is ALSO the exact name of a current room. With both docs present the
//  same label is claimed twice — which is precisely why the resolver ranks and
//  refuses instead of taking the first hit.
// ──────────────────────────────────────
export const ROOM_NAME_ALIASES = {
  "endo 1": "wc e1",
  "endo 2": "wc e2",
  "endo 3": "wc e3",
  "ob 1": "wc ob",
  "cath lab 1": "wc cl1",
  "cath lab 2": "wc cl2",
  "mri": "wc mri",
  "ct scan": "wc ct",
};

const norm = (s) => String(s ?? "").trim().toLowerCase();
const squash = (s) => norm(s).replace(/\s+/g, "");
/** Loosest comparison used for NEAR MISSES only — never to resolve. */
const loose = (s) => norm(s).replace(/[\s\-_./]+/g, "");

export const MATCH_RANKS = ["exact", "or-prefix", "alias", "squashed"];

/**
 * Rank how strongly `roomName` claims `label`. Lower is stronger; null = no
 * claim. Mirrors `assignmentsForRoom`'s rank order exactly, so a room resolved
 * here and a row matched there always agree.
 */
export function rankRoomMatch(roomName, label) {
  const room = norm(roomName);
  const lab = norm(label);
  if (!room || !lab) return null;
  if (lab === room) return 0;
  if (lab === room.replace(/^or\s+/, "")) return 1;
  if (ROOM_NAME_ALIASES[room] && lab === ROOM_NAME_ALIASES[room]) return 2;
  if (squash(room) && squash(lab) === squash(room)) return 3;
  return null;
}

/**
 * Resolve a sheet label to the ONE room doc it means.
 *
 * @param {string} label       the assignment's orRoom / the sheet's column A
 * @param {Array}  rooms       the facility's live room docs ({ id, name, … })
 * @returns {{
 *   label: string,
 *   room: object|null,
 *   status: "resolved"|"ambiguous"|"unresolved"|"empty",
 *   rank: number|null,
 *   how: string,
 *   candidates: Array<{ room: object, rank: number, how: string }>,
 *   nearMisses: Array<{ room: object, how: string }>,
 *   reason: string
 * }}
 */
export function resolveRoom(label, rooms) {
  const list = Array.isArray(rooms) ? rooms : [];
  const raw = String(label ?? "").trim();
  const base = {
    label: raw,
    room: null,
    status: "unresolved",
    rank: null,
    how: "",
    candidates: [],
    nearMisses: [],
    reason: "",
  };
  if (!raw) return { ...base, status: "empty", reason: "no room label on the row" };

  const scored = [];
  for (const room of list) {
    const rank = rankRoomMatch(room?.name, raw);
    if (rank != null) scored.push({ room, rank, how: MATCH_RANKS[rank] });
  }

  if (!scored.length) {
    return {
      ...base,
      nearMisses: nearMissRooms(raw, list),
      reason: `no room matches "${raw}"`,
    };
  }

  scored.sort((a, b) => a.rank - b.rank);
  const best = scored[0].rank;
  const winners = scored.filter((s) => s.rank === best);

  if (winners.length > 1) {
    // ⚠️ Two rooms claim it equally. Refuse — naming both — rather than let
    // document order decide which room a case lands in.
    return {
      ...base,
      status: "ambiguous",
      rank: best,
      how: MATCH_RANKS[best],
      candidates: winners,
      nearMisses: nearMissRooms(raw, list, winners),
      reason:
        `"${raw}" is claimed equally (${MATCH_RANKS[best]}) by ` +
        `${winners.map((w) => `"${w.room?.name}"`).join(" and ")}. ` +
        "Refusing to guess which room the case is in.",
    };
  }

  return {
    ...base,
    room: winners[0].room,
    status: "resolved",
    rank: best,
    how: MATCH_RANKS[best],
    candidates: scored,
  };
}

/**
 * Rooms that ALMOST claim the label — what a review item shows a human so they
 * can fix it in one look. Punctuation- and space-insensitive equality, plus a
 * shared-prefix/suffix relation, capped so a review card stays readable.
 */
export function nearMissRooms(label, rooms, exclude = []) {
  const raw = loose(label);
  if (!raw) return [];
  const skip = new Set(exclude.map((e) => e.room));
  const out = [];
  for (const room of Array.isArray(rooms) ? rooms : []) {
    if (skip.has(room)) continue;
    const name = loose(room?.name);
    if (!name) continue;
    let how = "";
    if (name === raw) how = "same once punctuation and spacing are ignored";
    else if (name.endsWith(raw)) how = `"${room.name}" is this label with a campus prefix`;
    else if (raw.endsWith(name)) how = `"${room.name}" is this label without its prefix`;
    if (how) out.push({ room, how });
    if (out.length >= 5) break;
  }
  return out;
}

/**
 * A review item for a label that did not resolve — the verbatim label, what
 * stopped it, and every candidate named.
 *
 * ⚠️ AN UNRESOLVED ROOM MUST NEVER SILENTLY VANISH. Today a row whose room
 * does not match is dropped by the global import and by the nightly job, and
 * the nightly job full-RESETS the board — so the day is rebuilt without it.
 */
export function roomReviewItem(verdict, extra = {}) {
  if (!verdict || verdict.status === "resolved") return null;
  return {
    kind: "room",
    label: verdict.label,
    status: verdict.status,
    reason: verdict.reason,
    candidates: (verdict.candidates || []).map((c) => ({
      id: c.room?.id ?? null,
      name: c.room?.name ?? "",
      how: c.how,
    })),
    nearMisses: (verdict.nearMisses || []).map((n) => ({
      id: n.room?.id ?? null,
      name: n.room?.name ?? "",
      how: n.how,
    })),
    ...extra,
  };
}

/**
 * Resolve one of the sheet's own routing arrows ("to TEE", "from OR 10") to a
 * room doc.
 *
 * ⚠️ THE ARROW MUST NEVER OVERRIDE AN EXPLICIT ROOM. This resolves the
 * DESTINATION of a hint; the row's own `orRoom` is decided elsewhere and is
 * not consulted except as the campus context that lets "TEE" be read as
 * "WC TEE" when the arrow was written on a Wesley Chapel row.
 *
 * @param {string} hint         the raw hint text ("TEE", "OR 6", "MRI")
 * @param {Array}  rooms        the facility's live room docs
 * @param {object} [opts]
 *   @param {string} [opts.contextLabel] the orRoom of the row the hint was
 *     written on — supplies the local dialect's campus
 *   @param {function} [opts.qualify] injected `qualifyHint` (roomCampus.js);
 *     omit and only the verbatim hint is tried
 * @returns {{room, status, how, tried:string[], reason:string}}
 */
export function resolveRoomHint(hint, rooms, opts = {}) {
  const raw = String(hint ?? "").trim();
  if (!raw) return { room: null, status: "empty", how: "", tried: [], reason: "no hint" };

  const qualify = typeof opts.qualify === "function" ? opts.qualify : null;
  const tried = qualify ? qualify(raw, opts.contextLabel) : [raw];

  for (let i = 0; i < tried.length; i += 1) {
    const verdict = resolveRoom(tried[i], rooms);
    if (verdict.status === "resolved") {
      return {
        room: verdict.room,
        status: "resolved",
        // i === 0 is the arrow exactly as the sheet wrote it; anything later
        // was reached only by reading it in the row's own campus dialect, and
        // a caller may want to hold that to a lower standard.
        how: i === 0 ? "verbatim" : "campus-qualified",
        tried,
        reason: "",
      };
    }
    if (verdict.status === "ambiguous") {
      return { room: null, status: "ambiguous", how: "", tried, reason: verdict.reason };
    }
  }
  return {
    room: null,
    status: "unresolved",
    how: "",
    tried,
    reason: `no room matches the arrow "${raw}"`,
  };
}
